import { getRegisteredPageLoaders, type RouteBundleGroup } from "@/routes/lazyPage";

interface BackgroundBundlePreloadOptions {
  groups: readonly RouteBundleGroup[];
  prepare?: () => Promise<void>;
  schedule?: (callback: () => void) => void;
  batchSize?: number;
}

interface RouteBundleSessionInput {
  clerkLoaded: boolean;
  authenticated: boolean;
  loadClientPathway: boolean;
  loadSfsPathway: boolean;
  loadReportingPathway: boolean;
}

const DEFAULT_BATCH_SIZE = 2;
const IDLE_TIMEOUT_MS = 2_000;

type RegisteredLoader = ReturnType<typeof getRegisteredPageLoaders>[number]["load"];

/**
 * Shared pages (and public pages, which register as shared) prefetch on every
 * cold start. Pathway page chunks wait until Clerk has resolved, then only the
 * pathways this session mounts are fetched. Client accounts do not download
 * SFS page chunks.
 */
export function routeBundleGroupsForSession(input: RouteBundleSessionInput): RouteBundleGroup[] {
  const groups: RouteBundleGroup[] = ["shared"];
  if (!input.clerkLoaded || !input.authenticated) {
    return groups;
  }
  if (input.loadClientPathway) {
    groups.push("client");
  }
  if (input.loadSfsPathway) {
    groups.push("sfs");
  }
  if (input.loadReportingPathway) {
    groups.push("reporting");
  }
  return groups;
}

/** Run after the document has loaded, then while the browser is idle. */
export function scheduleWhenIdle(callback: () => void): void {
  const start = (): void => {
    if (typeof window !== "undefined" && "requestIdleCallback" in window) {
      window.requestIdleCallback(() => callback(), { timeout: IDLE_TIMEOUT_MS });
      return;
    }
    setTimeout(callback, 200);
  };

  if (typeof document !== "undefined" && document.readyState !== "complete") {
    window.addEventListener("load", start, { once: true });
    return;
  }
  start();
}

/**
 * Fetches registered route chunks in small batches so first paint and the
 * current page's requests stay ahead of the prefetch.
 */
export function startBackgroundBundlePreload(options: BackgroundBundlePreloadOptions): () => void {
  let cancelled = false;
  const started = new Set<RegisteredLoader>();
  const schedule = options.schedule ?? scheduleWhenIdle;
  const batchSize = options.batchSize ?? DEFAULT_BATCH_SIZE;
  const groups = new Set(options.groups);

  const pump = (): void => {
    if (cancelled) {
      return;
    }
    const pending = getRegisteredPageLoaders().filter(
      (entry) => groups.has(entry.group) && !started.has(entry.load)
    );
    if (pending.length === 0) {
      return;
    }
    const batch = pending.slice(0, batchSize);
    for (const entry of batch) {
      started.add(entry.load);
    }
    void Promise.all(batch.map((entry) => entry.load().catch(() => undefined))).finally(() => {
      if (!cancelled) {
        schedule(pump);
      }
    });
  };

  const kick = (): void => {
    if (cancelled) {
      return;
    }
    const prepared = options.prepare ? options.prepare() : Promise.resolve();
    void prepared.finally(() => {
      if (!cancelled) {
        schedule(pump);
      }
    });
  };

  schedule(kick);

  return () => {
    cancelled = true;
  };
}
